import { ref, onUnmounted } from 'vue'

// WebSocket connection state
const ws = ref<WebSocket | null>(null)
const isConnected = ref(false)
const lastMessage = ref<any>(null)
const reconnectAttempts = ref(0)
const maxReconnectAttempts = 0 // 0 means unlimited retries
const reconnectDelay = 3000

// Message type definitions
export interface TradeNotification {
  type: 'trade_notification'
  payload: {
    order_id: number
    order_no: string
    status: 'completed' | 'failed'
    message: string
    market_title: string
    side: string
    amount: string
    shares: string
    fee: string
  }
}

export interface KYCNotification {
  type: 'kyc_notification'
  payload: {
    status: 'approved' | 'rejected'
    message: string
    kyc_level: string
  }
}

export interface MarketNotification {
  type: 'market_notification'
  payload: {
    id: number
    type: string
    title: string
    content: string
    market_id: number
    market_url: string
  }
}

export interface SystemNotice {
  type: 'system_notice'
  payload: any
}

// Event handlers
type TradeMessageHandler = (notification: TradeNotification) => void
type KYCMessageHandler = (notification: KYCNotification) => void
type MarketMessageHandler = (notification: MarketNotification) => void
const tradeHandlers: Set<TradeMessageHandler> = new Set()
const kycHandlers: Set<KYCMessageHandler> = new Set()
const marketHandlers: Set<MarketMessageHandler> = new Set()

// Connect to WebSocket
export function connectWebSocket(token: string) {
  if (ws.value?.readyState === WebSocket.OPEN) {
    console.log('WebSocket already connected')
    return
  }

  // Use environment variable for WebSocket URL, fallback to relative path
  const wsUrl = import.meta.env.VITE_WS_URL 
    ? `${import.meta.env.VITE_WS_URL}?token=${token}`
    : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws?token=${token}`
  
  try {
    ws.value = new WebSocket(wsUrl)
    
    ws.value.onopen = () => {
      console.log('WebSocket connected')
      isConnected.value = true
      reconnectAttempts.value = 0
      
      // Send heartbeat after connection
      sendHeartbeat()
    }
    
    ws.value.onmessage = (event) => {
      // Handle multiple messages (separated by newline)
      const messages = event.data.split('\n').filter((m: string) => m.trim())
      
      messages.forEach((messageStr: string) => {
        try {
          const message = JSON.parse(messageStr)
          lastMessage.value = message
          
          // Handle different message types
          if (message.type === 'trade_notification') {
            // Notify all trade handlers
            tradeHandlers.forEach(handler => {
              handler(message as TradeNotification)
            })
          } else if (message.type === 'kyc_notification') {
            // Notify all KYC handlers
            kycHandlers.forEach(handler => {
              handler(message as KYCNotification)
            })
          } else if (message.type === 'market_notification') {
            // Notify all market handlers
            marketHandlers.forEach(handler => {
              handler(message as MarketNotification)
            })
            // Also show as toast notification
            const payload = message.payload
            console.log('[WebSocket] Market notification received:', payload)
          } else if (message.type === 'heartbeat') {
            console.log('Heartbeat received')
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e)
        }
      })
    }
    
    ws.value.onclose = () => {
      console.log('WebSocket disconnected')
      isConnected.value = false
      ws.value = null
      
      // Attempt to reconnect (unlimited if maxReconnectAttempts is 0)
      const shouldReconnect = maxReconnectAttempts === 0 || reconnectAttempts.value < maxReconnectAttempts
      if (shouldReconnect) {
        if (maxReconnectAttempts > 0) {
          reconnectAttempts.value++
          console.log(`Attempting to reconnect (${reconnectAttempts.value}/${maxReconnectAttempts})...`)
        } else {
          console.log(`Attempting to reconnect (${reconnectAttempts.value + 1})...`)
          reconnectAttempts.value++
        }
        setTimeout(() => {
          const token = localStorage.getItem('access_token')
          if (token) {
            connectWebSocket(token)
          }
        }, reconnectDelay)
      }
    }
    
    ws.value.onerror = (error) => {
      console.error('WebSocket error:', error)
    }
  } catch (error) {
    console.error('Failed to create WebSocket:', error)
  }
}

// Disconnect WebSocket
export function disconnectWebSocket() {
  if (ws.value) {
    ws.value.close()
    ws.value = null
    isConnected.value = false
  }
}

// Send heartbeat
function sendHeartbeat() {
  if (ws.value?.readyState === WebSocket.OPEN) {
    ws.value.send(JSON.stringify({ type: 'heartbeat' }))
  }
}

// Subscribe to trade notifications
export function subscribeToTradeNotifications(handler: TradeMessageHandler) {
  tradeHandlers.add(handler)
  return () => {
    tradeHandlers.delete(handler)
  }
}

// Subscribe to KYC notifications
export function subscribeToKYCNotifications(handler: KYCMessageHandler) {
  kycHandlers.add(handler)
  return () => {
    kycHandlers.delete(handler)
  }
}

// Subscribe to market notifications
export function subscribeToMarketNotifications(handler: MarketMessageHandler) {
  marketHandlers.add(handler)
  return () => {
    marketHandlers.delete(handler)
  }
}

// Send a message through WebSocket
export function sendWebSocketMessage(message: any) {
  if (ws.value?.readyState === WebSocket.OPEN) {
    ws.value.send(JSON.stringify(message))
  } else {
    console.warn('WebSocket not connected, cannot send message')
  }
}

// Auto-reconnect on token change
export function initWebSocketAutoConnect() {
  const token = localStorage.getItem('access_token')
  if (token) {
    connectWebSocket(token)
  }
  
  // Listen for storage changes (for tab sync)
  window.addEventListener('storage', (event) => {
    if (event.key === 'access_token') {
      if (event.newValue) {
        connectWebSocket(event.newValue)
      } else {
        disconnectWebSocket()
      }
    }
  })
}
